var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import api from '@utils/axios';
import ResolveModal from '@features/Deficiencies/components/ResolveModal/ResolveModal';
import { translate } from '@utils/i18n';
import { IssueStatusEnum } from '../../types';
import ManagerNote from './ManagerNote';
import DeviceInfoToggle from './DeviceInfoToggle';
import IssueLayout from './IssueLayout';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import './styles.scss';
export default function UnresolvedPriorDeficienciesCard({ issue, defaultExpanded, onToggle, onDeviceInfoToggle, onUpdateIssue, onUndoAction, readOnly = false }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const status = issue.status || IssueStatusEnum.Detected;
    const [isDeficienciesExpanded, setIsDeficienciesExpanded] = useState(false);
    const [resolveDeficiencyId, setResolveDeficiencyId] = useState(null);
    const [localDeficiencies, setLocalDeficiencies] = useState(issue.issueData.priorDeficiencies);
    const [reopenInFlight, setReopenInFlight] = useState(false);
    // Note: No handleSuggestCorrection - this issue type doesn't support suggestions
    const handleDismissIssue = () => {
        onUpdateIssue(issue.id, IssueStatusEnum.DismissedByBackOffice, false);
    };
    const handleNoteChange = (note) => {
        onUpdateIssue(issue.id, status, issue.markedForSuggestion, note);
    };
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };
    const toggleDeficiencies = () => {
        setIsDeficienciesExpanded(!isDeficienciesExpanded);
    };
    const openResolveModal = (deficiencyId) => {
        setResolveDeficiencyId(deficiencyId);
    };
    const closeResolveModal = () => {
        setResolveDeficiencyId(null);
    };
    const handleDeficiencyResolved = () => __awaiter(this, void 0, void 0, function* () {
        if (!resolveDeficiencyId)
            return;
        const currentDeficiencyId = resolveDeficiencyId;
        closeResolveModal();
        try {
            // Fetch only the deficiency to get updated status
            const response = yield api.get(`/api/internal/deficiencies/${currentDeficiencyId}`);
            // Update local state with new resolution status
            setLocalDeficiencies((prev) => prev.map((def) => def.deficiencyId === currentDeficiencyId
                ? Object.assign(Object.assign({}, def), { isResolved: response.data.deficiency.isResolved, resolutionStatus: response.data.deficiency.resolutionStatusName || 'Resolved' }) : def));
        }
        catch (error) {
            console.error('Failed to fetch updated deficiency status:', error);
            // Silently fail - will be correct on next page load
        }
    });
    const handleReopenDeficiency = (deficiencyId) => __awaiter(this, void 0, void 0, function* () {
        if (reopenInFlight)
            return;
        setReopenInFlight(true);
        try {
            // Re-open the deficiency using the unresolve endpoint
            yield api.post(`/deficiencies/${deficiencyId}/unresolve.json`);
            // Fetch updated status
            const response = yield api.get(`/api/internal/deficiencies/${deficiencyId}`);
            // Update local state with response data
            setLocalDeficiencies((prev) => prev.map((def) => {
                var _a;
                return def.deficiencyId === deficiencyId
                    ? Object.assign(Object.assign({}, def), { isResolved: response.data.deficiency.isResolved, resolutionStatus: (_a = response.data.deficiency.resolutionStatusName) !== null && _a !== void 0 ? _a : def.resolutionStatus }) : def;
            }));
        }
        catch (error) {
            console.error('Failed to reopen deficiency:', error);
        }
        finally {
            setReopenInFlight(false);
        }
    });
    const handleResolveDeficiency = () => {
        if (localDeficiencies.length === 1) {
            openResolveModal(localDeficiencies[0].deficiencyId);
        }
    };
    const handleReopenFromHeader = () => {
        if (localDeficiencies.length === 1) {
            void handleReopenDeficiency(localDeficiencies[0].deficiencyId);
        }
    };
    // Determine which button to show in header for single deficiency
    const singleDeficiency = localDeficiencies.length === 1 ? localDeficiencies[0] : null;
    const showResolveInHeader = singleDeficiency && !singleDeficiency.isResolved;
    const showReopenInHeader = singleDeficiency && singleDeficiency.isResolved;
    return (React.createElement(IssueLayout, { issueType: "unresolved_deficiencies", status: status, defaultExpanded: defaultExpanded, onToggle: onToggle, onSuggestCorrection: () => {
            /* No-op: this issue type doesn't support suggestions */
        }, onDismissIssue: handleDismissIssue, onUndoAction: onUndoAction, readOnly: readOnly, statusChanges: issue.statusChanges, markedForSuggestion: false, hideSuggestCorrection: true, onResolveDeficiency: showResolveInHeader ? handleResolveDeficiency : undefined, onReopenDeficiency: showReopenInHeader ? handleReopenFromHeader : undefined },
        React.createElement("div", { className: "unresolved-prior-deficiencies" },
            React.createElement("p", { className: "unresolved-prior-deficiencies__description" }, "This device and question were marked as deficient in a previous inspection and remain unresolved."),
            React.createElement("div", { className: "line-separator" }),
            React.createElement("div", { className: "unresolved-prior-deficiencies__device-question" },
                React.createElement("div", { className: "unresolved-prior-deficiencies__device-name" },
                    React.createElement("strong", null,
                        humanizeInspectableType((_a = issue.inspectable) === null || _a === void 0 ? void 0 : _a.type),
                        ' ',
                        ((_c = (_b = issue.inspectable) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.identifier) ||
                            ((_e = (_d = issue.inspectable) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.name) ||
                            `#${issue.issueData.inspectableId}`)),
                React.createElement("div", { className: "unresolved-prior-deficiencies__question-text" }, issue.issueData.questionText)),
            React.createElement(DeviceInfoToggle, { inspectable: issue.inspectable, onToggle: onDeviceInfoToggle }),
            React.createElement("div", { className: "unresolved-prior-deficiencies__deficiencies-section" },
                React.createElement("button", { type: "button", className: "unresolved-prior-deficiencies__accordion-header", onClick: toggleDeficiencies, disabled: readOnly, "aria-expanded": isDeficienciesExpanded },
                    React.createElement("span", { className: "unresolved-prior-deficiencies__accordion-title" },
                        "Unresolved ",
                        localDeficiencies.length === 1 ? 'Deficiency' : 'Deficiencies'),
                    React.createElement("i", { className: `fa-light fa-angle-${isDeficienciesExpanded ? 'down' : 'right'}` })),
                isDeficienciesExpanded && (React.createElement("div", { className: "qmb-table qmb-table--02 qmb-table--x-full" },
                    React.createElement("table", null,
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", null, "Deficiency"),
                                React.createElement("th", null, "Inspection"),
                                React.createElement("th", null, "Date"),
                                React.createElement("th", null, "Age"),
                                React.createElement("th", null, "Resolution Status"),
                                localDeficiencies.length > 1 && React.createElement("th", null, "Actions"))),
                        React.createElement("tbody", null, localDeficiencies.map((prior) => (React.createElement("tr", { key: prior.deficiencyId },
                            React.createElement("td", null,
                                React.createElement("a", { href: `/deficiencies/${prior.deficiencyId}`, target: "_blank", rel: "noreferrer" },
                                    "#",
                                    prior.deficiencyId)),
                            React.createElement("td", null,
                                React.createElement("a", { href: `/inspections/${prior.inspectionId}`, target: "_blank", rel: "noreferrer" },
                                    "#",
                                    prior.inspectionId)),
                            React.createElement("td", null, formatDate(prior.inspectionDate)),
                            React.createElement("td", null,
                                prior.ageDays,
                                " days old"),
                            React.createElement("td", null, prior.isResolved ? prior.resolutionStatus || 'Resolved' : 'Unresolved'),
                            localDeficiencies.length > 1 && (React.createElement("td", null, prior.isResolved ? (React.createElement("button", { type: "button", disabled: readOnly, className: "qmb-control qmb-control--sm qmb-control--default", onClick: () => {
                                    void handleReopenDeficiency(prior.deficiencyId);
                                } },
                                React.createElement("i", { className: "fa-light fa-rotate-left" }),
                                translate('reopen', { namespace: 'features.iim_review.issue_card' }))) : (React.createElement("button", { type: "button", disabled: readOnly, className: "qmb-control qmb-control--sm qmb-control--default", onClick: () => {
                                    openResolveModal(prior.deficiencyId);
                                } },
                                React.createElement("i", { className: "fa-light fa-check-circle" }),
                                translate('resolve_deficiency', { namespace: 'features.iim_review.issue_card' }))))))))))))),
            issue.markedForSuggestion && (!readOnly || ((_f = issue.managerNote) === null || _f === void 0 ? void 0 : _f.trim())) && (React.createElement("div", { className: "unresolved-prior-deficiencies__manager-note" },
                React.createElement(ManagerNote, { note: (_g = issue.managerNote) !== null && _g !== void 0 ? _g : '', onNoteChange: handleNoteChange, disabled: readOnly })))),
        resolveDeficiencyId && (React.createElement(ResolveModal, { toggleDialog: closeResolveModal, deficiencyId: String(resolveDeficiencyId !== null && resolveDeficiencyId !== void 0 ? resolveDeficiencyId : ''), onFinish: () => {
                void handleDeficiencyResolved();
            } }))));
}
